import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

export interface IamRoleSummary {
  id: number;
  name: string;
  description?: string | null;
  menusCount?: number;
  employeesCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface IamRoleDetail extends IamRoleSummary {
  menuIds: number[];
  menus?: any[];
  employees?: any[];
  employeeIds?: number[];
}

export interface IamMenu {
  id: number;
  name: string;
  route?: string;
  visible?: boolean;
  fromApp?: boolean;
  menuId?: number | null;
}

export interface MenuTreeResponse {
  employee?: { id: number; name: string; surname: string; email: string };
  menuArbol: any[];
  menuIds: number[];
  directMenuIds: number[];
  roleMenuIds: number[];
  roles: Array<{ id: number; name: string; description: string | null }>;
  inheritedByMenuId: Record<number, string[]>;
}

@Injectable({
  providedIn: 'root',
})
export class IamService {
  private readonly baseUrl = environment.administrativoBaseUrl;

  constructor(private readonly http: HttpClient) {}

  async getRoles(): Promise<IamRoleSummary[]> {
    const response = await this.http
      .get<{ ok?: boolean; data?: IamRoleSummary[] }>(`${this.baseUrl}/employees/roles`)
      .toPromise();
    return response?.data ?? [];
  }

  async getRoleById(id: number): Promise<IamRoleDetail | null> {
    const response = await this.http
      .get<{ ok?: boolean; data?: IamRoleDetail }>(`${this.baseUrl}/employees/roles/${id}`)
      .toPromise();
    return response?.data ?? null;
  }

  async createRole(body: { name: string; description?: string | null }): Promise<any> {
    return this.http.post(`${this.baseUrl}/employees/roles`, body).toPromise();
  }

  async updateRole(
    id: number,
    body: { name?: string; description?: string | null },
  ): Promise<any> {
    return this.http.post(`${this.baseUrl}/employees/roles/${id}`, body).toPromise();
  }

  async deleteRole(id: number): Promise<any> {
    return this.http.delete(`${this.baseUrl}/employees/roles/${id}`).toPromise();
  }

  async setRoleMenus(roleId: number, menuIds: number[]): Promise<any> {
    return this.http
      .post(`${this.baseUrl}/employees/roles/${roleId}/menus`, { menuIds })
      .toPromise();
  }

  async setRoleEmployees(roleId: number, employeeIds: number[]): Promise<any> {
    return this.http
      .post(`${this.baseUrl}/employees/roles/${roleId}/employees`, { employeeIds })
      .toPromise();
  }

  async getEmployeeRoles(employeeId: number): Promise<IamRoleSummary[]> {
    const response = await this.http
      .get<{ ok?: boolean; data?: IamRoleSummary[] }>(
        `${this.baseUrl}/employees/${employeeId}/roles`,
      )
      .toPromise();
    return response?.data ?? [];
  }

  async setEmployeeRoles(employeeId: number, roleIds: number[]): Promise<any> {
    return this.http
      .post(`${this.baseUrl}/employees/${employeeId}/roles`, { roleIds })
      .toPromise();
  }

  async getEmployeeMenuTree(employeeId: number): Promise<MenuTreeResponse | null> {
    const response = await this.http
      .get<{ ok?: boolean; data?: MenuTreeResponse }>(
        `${this.baseUrl}/employees/${employeeId}/menu-tree`,
      )
      .toPromise();
    return response?.data ?? null;
  }

  async getMyMenuTree(): Promise<MenuTreeResponse | null> {
    const response = await this.http
      .get<{ ok?: boolean; data?: MenuTreeResponse }>(
        `${this.baseUrl}/employees/me/menu-tree`,
      )
      .toPromise();
    return response?.data ?? null;
  }

  async getAllMenus(): Promise<IamMenu[]> {
    const response = await this.http
      .get<{ data?: IamMenu[] }>(`${this.baseUrl}/employees/menus`)
      .toPromise();
    return response?.data ?? [];
  }

  async createMenu(body: {
    name: string;
    route: string;
    visible?: boolean;
    fromApp?: boolean;
    menuId?: number | null;
  }): Promise<any> {
    return this.http.post(`${this.baseUrl}/employees/menus`, body).toPromise();
  }

  async updateMenu(
    id: number,
    body: {
      name?: string;
      route?: string;
      visible?: boolean;
      fromApp?: boolean;
      menuId?: number | null;
    },
  ): Promise<any> {
    return this.http.post(`${this.baseUrl}/employees/menus/${id}`, body).toPromise();
  }

  async deleteMenu(id: number): Promise<any> {
    return this.http.delete(`${this.baseUrl}/employees/menus/${id}`).toPromise();
  }

  async seedAdminRole(employeeId: number): Promise<any> {
    return this.http
      .post(`${this.baseUrl}/employees/roles/seed-admin`, { employeeId })
      .toPromise();
  }
}
